import React, { useState, useEffect, useRef } from 'react';
import { Send, Phone, Video, Paperclip, Smile, Mic, Camera, MapPin, Calendar, CreditCard, Package, Users, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

interface Message {
  id: string;
  type: 'text' | 'image' | 'file' | 'quick_reply' | 'interactive' | 'location' | 'payment' | 'booking';
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  status?: 'sending' | 'sent' | 'delivered' | 'read';
  quickReplies?: string[];
  interactive?: {
    type: 'button' | 'list' | 'calendar' | 'payment';
    options?: Array<{ id: string; title: string; description?: string; }>;
  };
  metadata?: any;
}

interface QuickAction {
  id: string;
  icon: React.ReactNode;
  label: string;
  color: string;
  action: string;
}

export default function LiveChat() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'text',
      content: 'Welcome to LA Mirage! I\'m your AI wedding assistant. How can I help you today?',
      sender: 'ai',
      timestamp: new Date(),
      quickReplies: ['View Packages', 'Check Availability', 'Get Pricing', 'Virtual Tour']
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const quickActions: QuickAction[] = [
    { id: 'packages', icon: <Package className="w-4 h-4" />, label: 'Packages', color: 'bg-blue-500', action: 'show_packages' },
    { id: 'calendar', icon: <Calendar className="w-4 h-4" />, label: 'Book Date', color: 'bg-green-500', action: 'check_availability' },
    { id: 'payment', icon: <CreditCard className="w-4 h-4" />, label: 'Pay Invoice', color: 'bg-purple-500', action: 'view_invoices' },
    { id: 'guests', icon: <Users className="w-4 h-4" />, label: 'Guest List', color: 'bg-orange-500', action: 'manage_guests' },
    { id: 'location', icon: <MapPin className="w-4 h-4" />, label: 'Venue Tour', color: 'bg-red-500', action: 'virtual_tour' },
    { id: 'support', icon: <Phone className="w-4 h-4" />, label: 'Call Now', color: 'bg-indigo-500', action: 'call_support' }
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (content: string, type: string = 'text') => {
    if (!content.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      type: type as any,
      content,
      sender: 'user',
      timestamp: new Date(),
      status: 'sending'
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const response = await fetch('/api/ai-chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: content, type })
      });

      const aiResponse = await response.json();
      
      setTimeout(() => {
        setIsTyping(false);
        setMessages(prev => [
          ...prev.map(msg => msg.id === newMessage.id ? { ...msg, status: 'read' } : msg),
          {
            id: Date.now().toString(),
            type: aiResponse.type || 'text',
            content: aiResponse.content,
            sender: 'ai',
            timestamp: new Date(),
            quickReplies: aiResponse.quickReplies,
            interactive: aiResponse.interactive
          }
        ]);
      }, 1000);
    } catch (error) {
      setIsTyping(false);
      console.error('Error sending message:', error);
    }
  };

  const handleQuickReply = (reply: string) => {
    sendMessage(reply);
  };

  const handleQuickAction = (action: string) => {
    const actionMessages = {
      show_packages: 'Show me all wedding packages',
      check_availability: 'Check available dates',
      view_invoices: 'Show my invoices',
      manage_guests: 'Manage guest list',
      virtual_tour: 'Start virtual venue tour',
      call_support: 'Connect me with support team'
    };
    
    sendMessage(actionMessages[action as keyof typeof actionMessages] || action);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Handle file upload
      sendMessage(`Uploaded: ${file.name}`, 'file');
    }
  };

  const startVoiceRecording = () => {
    setIsRecording(true);
    // Implement voice recording logic
    setTimeout(() => {
      setIsRecording(false);
      sendMessage('Voice message recorded', 'text');
    }, 2000);
  };

  const renderMessage = (message: Message) => {
    const isUser = message.sender === 'user';
    
    return (
      <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-end space-x-2 max-w-[80%]`}>
          {!isUser && (
            <Avatar className="w-8 h-8">
              <AvatarImage src="/api/placeholder/32/32" alt="AI Assistant" />
              <AvatarFallback className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs">AI</AvatarFallback>
            </Avatar>
          )}
          
          <div className={`rounded-2xl px-4 py-3 ${
            isUser 
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
          }`}>
            <p className="text-sm">{message.content}</p>
            
            {message.quickReplies && (
              <div className="mt-3 flex flex-wrap gap-2">
                {message.quickReplies.map((reply, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs border-white/20 hover:bg-white/10"
                    onClick={() => handleQuickReply(reply)}
                  >
                    {reply}
                  </Button>
                ))}
              </div>
            )}
            
            {message.interactive && (
              <div className="mt-3">
                {message.interactive.type === 'button' && (
                  <div className="space-y-2">
                    {message.interactive.options?.map((option) => (
                      <Button
                        key={option.id}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => handleQuickReply(option.title)}
                      >
                        {option.title}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs opacity-70">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              {isUser && message.status && (
                <div className="flex space-x-1">
                  <div className={`w-1 h-1 rounded-full ${
                    message.status === 'read' ? 'bg-blue-300' : 'bg-gray-300'
                  }`} />
                  <div className={`w-1 h-1 rounded-full ${
                    message.status === 'read' ? 'bg-blue-300' : 'bg-gray-300'
                  }`} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Avatar className="w-10 h-10 border-2 border-white/20">
              <AvatarImage src="/api/placeholder/40/40" alt="LA Mirage AI" />
              <AvatarFallback className="bg-white/20 text-white">LA</AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-semibold">LA Mirage AI Assistant</h1>
              <p className="text-xs opacity-90">
                {isTyping ? 'Typing...' : 'Online • Responds instantly'}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
              <Phone className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
              <Video className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {showQuickActions && (
        <div className="p-4 bg-white dark:bg-gray-800 border-b">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {quickActions.map((action) => (
              <Button
                key={action.id}
                variant="outline"
                size="sm"
                className="h-auto flex-col py-3 space-y-1 hover:scale-105 transition-transform"
                onClick={() => handleQuickAction(action.action)}
              >
                <div className={`w-8 h-8 rounded-full ${action.color} flex items-center justify-center text-white`}>
                  {action.icon}
                </div>
                <span className="text-xs">{action.label}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {messages.map(renderMessage)}
        {isTyping && (
          <div className="flex justify-start mb-4">
            <div className="flex items-end space-x-2">
              <Avatar className="w-8 h-8">
                <AvatarImage src="/api/placeholder/32/32" alt="AI Assistant" />
                <AvatarFallback className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs">AI</AvatarFallback>
              </Avatar>
              <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl px-4 py-3">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 bg-white dark:bg-gray-800 border-t">
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="text-gray-500 hover:text-gray-700"
          >
            <Paperclip className="w-5 h-5" />
          </Button>
          
          <div className="flex-1 relative">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type a message..."
              onKeyPress={(e) => e.key === 'Enter' && sendMessage(inputMessage)}
              className="pr-10 rounded-full border-gray-300 focus:border-amber-500"
            />
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              <Smile className="w-4 h-4" />
            </Button>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={startVoiceRecording}
            className={`text-gray-500 hover:text-gray-700 ${isRecording ? 'bg-red-100 text-red-500' : ''}`}
          >
            <Mic className="w-5 h-5" />
          </Button>

          <Button
            onClick={() => sendMessage(inputMessage)}
            disabled={!inputMessage.trim()}
            className="rounded-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileUpload}
          accept="image/*,.pdf,.doc,.docx"
        />
      </div>
    </div>
  );
}